import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import {
  ArrowLeftIcon,
  CubeIcon,
  CalendarIcon,
  BuildingStorefrontIcon,
  BeakerIcon,
  CurrencyDollarIcon,
  PencilIcon,
  EyeIcon,
  TrashIcon,
  DocumentArrowDownIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import StatusBadge from '../components/StatusBadge';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [batches, setBatches] = useState([]);
  const [filteredBatches, setFilteredBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddBatchModal, setShowAddBatchModal] = useState(false);
  const [showEditBatchModal, setShowEditBatchModal] = useState(false);
  const [showBatchDetailsModal, setShowBatchDetailsModal] = useState(false);
  const [showDeleteBatchDialog, setShowDeleteBatchDialog] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0
  });
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    supplier: '',
    from_date: '',
    to_date: '',
    low_stock: false
  });

  const [editForm, setEditForm] = useState({
    name: '',
    brand: '',
    category: '',
    manufacturer: '',
    bottles_per_case: 12,
    unit_volume_ml: '',
    description: ''
  });

  // Updated batch form - removed batch_number field (will be auto-generated by backend)
  const [batchForm, setBatchForm] = useState({
    depot_id: '',
    supplier: '',
    purchase_date: format(new Date(), 'yyyy-MM-dd'),
    initial_quantity_cases: '',
    cost_per_case: '',
    price_per_case: '',
    expiry_date: ''
  });

  const [depots, setDepots] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => {
    fetchProduct();
    fetchBatches();
    fetchDepots();
  }, [id]);

  useEffect(() => {
    filterBatches();
  }, [batches, filters, pagination.page, pagination.per_page]);

  const fetchProduct = async () => {
    try {
      const response = await api.get(`/products/${id}`);
      setProduct(response.data);
      setEditForm({
        name: response.data.name,
        brand: response.data.brand || '',
        category: response.data.category || '',
        manufacturer: response.data.manufacturer || '',
        bottles_per_case: response.data.bottles_per_case,
        unit_volume_ml: response.data.unit_volume_ml || '',
        description: response.data.description || ''
      });
    } catch (error) {
      toast.error('Failed to fetch product');
      navigate('/inventory');
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await api.get(`/products/${id}/batches`);
      setBatches(response.data.items || []);
      
      // Extract unique suppliers for filter
      const uniqueSuppliers = [...new Set(response.data.items.map(b => b.supplier).filter(Boolean))];
      setSuppliers(uniqueSuppliers);
    } catch (error) {
      console.error('Failed to fetch batches:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepots = async () => {
    try {
      const response = await api.get('/depot/');
      setDepots(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch depots:', error);
    }
  };

  const filterBatches = () => {
    let filtered = [...batches];

    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(batch => 
        batch.batch_number?.toLowerCase().includes(searchLower) ||
        batch.supplier?.toLowerCase().includes(searchLower)
      );
    }

    // Apply supplier filter
    if (filters.supplier) {
      filtered = filtered.filter(batch => batch.supplier === filters.supplier);
    }

    // Apply date range filters
    if (filters.from_date) {
      filtered = filtered.filter(batch => 
        new Date(batch.purchase_date) >= new Date(filters.from_date)
      );
    }
    if (filters.to_date) {
      filtered = filtered.filter(batch => 
        new Date(batch.purchase_date) <= new Date(filters.to_date)
      );
    }

    // Apply low stock filter (less than 10 cases)
    if (filters.low_stock) {
      filtered = filtered.filter(batch => batch.current_quantity_cases < 10);
    }

    // Update total count
    setPagination(prev => ({ ...prev, total: filtered.length }));

    // Apply pagination
    const start = (pagination.page - 1) * pagination.per_page;
    const end = start + pagination.per_page;
    setFilteredBatches(filtered.slice(start, end));
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/products/${id}`, editForm);
      toast.success('Product updated successfully');
      setShowEditModal(false);
      fetchProduct();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update product');
    }
  };

  const handleAddBatch = async (e) => {
    e.preventDefault();
    try {
      // Remove any batch_number from the data if present - let backend generate it
      const submissionData = { ...batchForm };
      
      await api.post(`/products/${id}/batches`, submissionData);
      toast.success('Batch added successfully with auto-generated batch number');
      setShowAddBatchModal(false);
      resetBatchForm();
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add batch');
    }
  };

  const handleEditBatch = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/products/batches/${selectedBatch.id}`, batchForm);
      toast.success('Batch updated successfully');
      setShowEditBatchModal(false);
      resetBatchForm();
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update batch');
    }
  };

  const handleDeleteBatch = async () => {
    try {
      await api.delete(`/products/batches/${selectedBatch.id}`);
      toast.success('Batch deleted successfully');
      setShowDeleteBatchDialog(false);
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete batch');
    }
  };

  const handleViewBatch = (batch) => {
    setSelectedBatch(batch);
    setShowBatchDetailsModal(true);
  };

  const handleExport = async (format) => {
    try {
      const response = await api.get(`/products/${id}/batches/export`, {
        params: { format },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `batches_${product?.name}_${format}.${format === 'excel' ? 'xlsx' : format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Batches exported successfully');
    } catch (error) {
      toast.error('Failed to export batches');
    }
  };

  const resetBatchForm = () => {
    setBatchForm({
      depot_id: '',
      supplier: '',
      purchase_date: format(new Date(), 'yyyy-MM-dd'),
      initial_quantity_cases: '',
      cost_per_case: '',
      price_per_case: '',
      expiry_date: ''
    });
    setSelectedBatch(null);
  };

  const openEditBatch = (batch) => {
    setSelectedBatch(batch);
    setBatchForm({
      depot_id: batch.depot_id || '',
      supplier: batch.supplier || '',
      purchase_date: batch.purchase_date ? format(new Date(batch.purchase_date), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
      initial_quantity_cases: batch.initial_quantity_cases,
      cost_per_case: batch.cost_per_case,
      price_per_case: batch.price_per_case,
      expiry_date: batch.expiry_date ? format(new Date(batch.expiry_date), 'yyyy-MM-dd') : ''
    });
    setShowEditBatchModal(true);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      supplier: '',
      from_date: '',
      to_date: '',
      low_stock: false
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const canManageBatches = () => {
    return user?.user_type === 'owner' || user?.user_type === 'manager';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!product) return null;

  const totalStock = batches.reduce((sum, batch) => sum + batch.current_quantity_cases, 0);
  const totalValue = batches.reduce((sum, batch) => 
    sum + (batch.current_quantity_cases * batch.cost_per_case), 0
  );
  const totalInitialStock = batches.reduce((sum, batch) => sum + batch.initial_quantity_cases, 0);

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/inventory')}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Inventory
        </button>
        
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center">
              <CubeIcon className="h-8 w-8 text-gray-400 mr-3" />
              <div>
                <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">
                  {product.name}
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  {product.brand} {product.category && `• ${product.category}`}
                </p>
              </div>
            </div>
          </div>
          <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
            {canManageBatches() && (
              <button
                onClick={() => {
                  resetBatchForm();
                  setShowAddBatchModal(true);
                }}
                className="btn-primary"
              >
                Add Stock
              </button>
            )}
            <button
              onClick={() => setShowEditModal(true)}
              className="btn-secondary"
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              Edit Product
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-4 mb-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">Total Initial Stock</dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">{totalInitialStock} cases</dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">Current Stock</dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">{totalStock} cases</dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">Stock Utilization</dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">
              {totalInitialStock > 0 ? Math.round(((totalInitialStock - totalStock) / totalInitialStock) * 100) : 0}%
            </dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">Inventory Value</dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">ZMW {totalValue.toLocaleString()}</dd>
          </div>
        </div>
      </div>

      {/* Product Details */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Product Details</h3>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-200">
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Product Name</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{product.name}</dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Brand</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{product.brand || '-'}</dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Category</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{product.category || '-'}</dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Manufacturer</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{product.manufacturer || '-'}</dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Volume per Bottle</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {product.unit_volume_ml ? `${product.unit_volume_ml} ml` : '-'}
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Bottles per Case</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{product.bottles_per_case}</dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Description</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {product.description || '-'}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Stock Batches Section */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">Stock Batches</h3>
            <p className="mt-1 text-sm text-gray-500">
              Manage batch-level inventory and track stock movements.
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => handleExport('excel')}
              className="btn-secondary text-sm"
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
              Export
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn-secondary text-sm"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Filters
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="border-t border-gray-200 px-4 py-4 sm:px-6 bg-gray-50">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-5">
              <div>
                <label className="block text-xs font-medium text-gray-700">Search</label>
                <input
                  type="text"
                  placeholder="Batch # or Supplier..."
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700">Supplier</label>
                <select
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={filters.supplier}
                  onChange={(e) => setFilters({...filters, supplier: e.target.value})}
                >
                  <option value="">All Suppliers</option>
                  {suppliers.map(supplier => (
                    <option key={supplier} value={supplier}>{supplier}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700">From Date</label>
                <input
                  type="date"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={filters.from_date}
                  onChange={(e) => setFilters({...filters, from_date: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700">To Date</label>
                <input
                  type="date"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={filters.to_date}
                  onChange={(e) => setFilters({...filters, to_date: e.target.value})}
                />
              </div>
              <div className="flex items-end space-x-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    checked={filters.low_stock}
                    onChange={(e) => setFilters({...filters, low_stock: e.target.checked})}
                  />
                  <span className="ml-2 text-sm text-gray-700">Low Stock Only</span>
                </label>
                <button
                  onClick={clearFilters}
                  className="text-sm text-primary-600 hover:text-primary-800"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Batches Table */}
        <div className="border-t border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Batch Number
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Supplier
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Purchase Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Initial Stock
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Stock
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cost/Case
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price/Case
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expiry Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredBatches.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-6 py-8 text-center text-sm text-gray-500">
                      No stock batches found
                    </td>
                  </tr>
                ) : (
                  filteredBatches.map((batch) => {
                    const isLowStock = batch.current_quantity_cases < 10;
                    const isExpiringSoon = batch.expiry_date && 
                      new Date(batch.expiry_date) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                    
                    return (
                      <tr key={batch.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {batch.batch_number || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {batch.supplier || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {batch.purchase_date ? format(new Date(batch.purchase_date), 'dd MMM yyyy') : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {batch.initial_quantity_cases} cases
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <span className={isLowStock ? 'text-red-600 font-medium' : 'text-gray-900'}>
                            {batch.current_quantity_cases} cases
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          ZMW {batch.cost_per_case.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          ZMW {batch.price_per_case.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {batch.expiry_date ? (
                            <span className={isExpiringSoon ? 'text-orange-600 font-medium' : ''}>
                              {format(new Date(batch.expiry_date), 'dd MMM yyyy')}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {isLowStock ? (
                            <StatusBadge status="low_stock" />
                          ) : isExpiringSoon ? (
                            <StatusBadge status="expiring_soon" />
                          ) : (
                            <StatusBadge status="in_stock" />
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleViewBatch(batch)}
                              className="text-blue-600 hover:text-blue-900"
                              title="View Details"
                            >
                              <EyeIcon className="h-5 w-5" />
                            </button>
                            {canManageBatches() && (
                              <>
                                <button
                                  onClick={() => openEditBatch(batch)}
                                  className="text-green-600 hover:text-green-900"
                                  title="Edit Batch"
                                >
                                  <PencilIcon className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedBatch(batch);
                                    setShowDeleteBatchDialog(true);
                                  }}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete Batch"
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page * pagination.per_page >= pagination.total}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(pagination.page - 1) * pagination.per_page + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(pagination.page * pagination.per_page, pagination.total)}
                  </span>{' '}
                  of <span className="font-medium">{pagination.total}</span> results
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <select
                  value={pagination.per_page}
                  onChange={(e) => setPagination({ page: 1, per_page: parseInt(e.target.value), total: pagination.total })}
                  className="border border-gray-300 rounded-md shadow-sm py-1 px-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="5">5 per page</option>
                  <option value="10">10 per page</option>
                  <option value="25">25 per page</option>
                  <option value="50">50 per page</option>
                  <option value="100">100 per page</option>
                </select>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: 1 }))}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <span className="sr-only">First</span>
                    <ChevronLeftIcon className="h-5 w-5" />
                    <ChevronLeftIcon className="h-5 w-5 -ml-3" />
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    Page {pagination.page} of {Math.ceil(pagination.total / pagination.per_page)}
                  </span>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page * pagination.per_page >= pagination.total}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.ceil(pagination.total / pagination.per_page) }))}
                    disabled={pagination.page * pagination.per_page >= pagination.total}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <span className="sr-only">Last</span>
                    <ChevronRightIcon className="h-5 w-5" />
                    <ChevronRightIcon className="h-5 w-5 -ml-3" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Batch Details Modal */}
      <Modal
        isOpen={showBatchDetailsModal}
        onClose={() => {
          setShowBatchDetailsModal(false);
          setSelectedBatch(null);
        }}
        title={`Batch Details - ${selectedBatch?.batch_number || 'N/A'}`}
        size="lg"
      >
        {selectedBatch && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-500">Batch Number</p>
                <p className="text-lg font-medium">{selectedBatch.batch_number || 'N/A'}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-500">Supplier</p>
                <p className="text-lg font-medium">{selectedBatch.supplier || 'N/A'}</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-600">Initial Stock</p>
                <p className="text-2xl font-bold text-blue-700">{selectedBatch.initial_quantity_cases} cases</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-green-600">Current Stock</p>
                <p className="text-2xl font-bold text-green-700">{selectedBatch.current_quantity_cases} cases</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm text-purple-600">Stock Dispatched</p>
                <p className="text-2xl font-bold text-purple-700">
                  {selectedBatch.initial_quantity_cases - selectedBatch.current_quantity_cases} cases
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Purchase Date</p>
                <p className="text-base font-medium">
                  {selectedBatch.purchase_date ? format(new Date(selectedBatch.purchase_date), 'dd MMMM yyyy') : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Expiry Date</p>
                <p className="text-base font-medium">
                  {selectedBatch.expiry_date ? format(new Date(selectedBatch.expiry_date), 'dd MMMM yyyy') : 'No expiry'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Cost per Case</p>
                <p className="text-xl font-bold text-gray-900">ZMW {selectedBatch.cost_per_case.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Selling Price per Case</p>
                <p className="text-xl font-bold text-gray-900">ZMW {selectedBatch.price_per_case.toFixed(2)}</p>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-500 mb-2">Profit Margin per Case</p>
              <p className="text-2xl font-bold text-green-600">
                ZMW {(selectedBatch.price_per_case - selectedBatch.cost_per_case).toFixed(2)}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {((selectedBatch.price_per_case - selectedBatch.cost_per_case) / selectedBatch.cost_per_case * 100).toFixed(1)}% markup
              </p>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm text-gray-500">Status</p>
              <div className="mt-2">
                {selectedBatch.current_quantity_cases < 10 ? (
                  <StatusBadge status="low_stock" />
                ) : selectedBatch.expiry_date && new Date(selectedBatch.expiry_date) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) ? (
                  <StatusBadge status="expiring_soon" />
                ) : (
                  <StatusBadge status="in_stock" />
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Product Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Product"
      >
        <form onSubmit={handleUpdateProduct}>
          <div className="space-y-4">
            <div>
              <label className="form-label">Product Name *</label>
              <input
                type="text"
                required
                className="form-input"
                value={editForm.name}
                onChange={(e) => setEditForm({...editForm, name: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Brand</label>
                <input
                  type="text"
                  className="form-input"
                  value={editForm.brand}
                  onChange={(e) => setEditForm({...editForm, brand: e.target.value})}
                />
              </div>
              <div>
                <label className="form-label">Category</label>
                <input
                  type="text"
                  className="form-input"
                  value={editForm.category}
                  onChange={(e) => setEditForm({...editForm, category: e.target.value})}
                />
              </div>
            </div>

            <div>
              <label className="form-label">Manufacturer</label>
              <input
                type="text"
                className="form-input"
                value={editForm.manufacturer}
                onChange={(e) => setEditForm({...editForm, manufacturer: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Bottles per Case *</label>
                <input
                  type="number"
                  min="1"
                  required
                  className="form-input"
                  value={editForm.bottles_per_case}
                  onChange={(e) => setEditForm({...editForm, bottles_per_case: parseInt(e.target.value)})}
                />
              </div>
              <div>
                <label className="form-label">Volume per Bottle (ml)</label>
                <input
                  type="number"
                  min="1"
                  className="form-input"
                  value={editForm.unit_volume_ml}
                  onChange={(e) => setEditForm({...editForm, unit_volume_ml: parseInt(e.target.value)})}
                />
              </div>
            </div>

            <div>
              <label className="form-label">Description</label>
              <textarea
                rows="3"
                className="form-input"
                value={editForm.description}
                onChange={(e) => setEditForm({...editForm, description: e.target.value})}
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                Update Product
              </button>
            </div>
          </div>
        </form>
      </Modal>

      {/* Add Batch Modal - Updated to match Inventory.js style (no batch number field) */}
      <Modal
        isOpen={showAddBatchModal}
        onClose={() => {
          setShowAddBatchModal(false);
          resetBatchForm();
        }}
        title="Add Stock Batch"
        size="lg"
      >
        <form onSubmit={handleAddBatch}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Depot *</label>
                <select
                  required
                  className="form-input"
                  value={batchForm.depot_id}
                  onChange={(e) => setBatchForm({...batchForm, depot_id: e.target.value})}
                >
                  <option value="">Select Depot</option>
                  {depots.map(depot => (
                    <option key={depot.id} value={depot.id}>{depot.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label">Supplier</label>
                <input
                  type="text"
                  className="form-input"
                  value={batchForm.supplier}
                  onChange={(e) => setBatchForm({...batchForm, supplier: e.target.value})}
                  placeholder="Enter supplier name"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Purchase Date *</label>
                <input
                  type="date"
                  required
                  className="form-input"
                  value={batchForm.purchase_date}
                  onChange={(e) => setBatchForm({...batchForm, purchase_date: e.target.value})}
                  max={format(new Date(), 'yyyy-MM-dd')}
                />
              </div>

              <div>
                <label className="form-label">Expiry Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={batchForm.expiry_date}
                  onChange={(e) => setBatchForm({...batchForm, expiry_date: e.target.value})}
                  min={batchForm.purchase_date}
                />
              </div>
            </div>

            <div>
              <label className="form-label">Quantity (Cases) *</label>
              <input
                type="number"
                min="1"
                required
                className="form-input"
                value={batchForm.initial_quantity_cases}
                onChange={(e) => setBatchForm({...batchForm, initial_quantity_cases: parseInt(e.target.value)})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Cost per Case (ZMW) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  className="form-input"
                  value={batchForm.cost_per_case}
                  onChange={(e) => setBatchForm({...batchForm, cost_per_case: parseFloat(e.target.value)})}
                />
              </div>

              <div>
                <label className="form-label">Selling Price per Case (ZMW) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  className="form-input"
                  value={batchForm.price_per_case}
                  onChange={(e) => setBatchForm({...batchForm, price_per_case: parseFloat(e.target.value)})}
                />
              </div>
            </div>

            {/* Info message about auto-generated batch number */}
            {/*
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start">
                <InformationCircleIcon className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700">
                  <span className="font-medium">Note:</span> A unique batch number will be automatically generated by the system for this stock entry.
               
                </p>
              </div>
            </div>
            */}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowAddBatchModal(false);
                  resetBatchForm();
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                Add Batch
              </button>
            </div>
          </div>
        </form>
      </Modal>

      {/* Edit Batch Modal */}
      <Modal
        isOpen={showEditBatchModal}
        onClose={() => {
          setShowEditBatchModal(false);
          resetBatchForm();
        }}
        title="Edit Stock Batch"
        size="lg"
      >
        <form onSubmit={handleEditBatch}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Depot *</label>
                <select
                  required
                  className="form-input"
                  value={batchForm.depot_id}
                  onChange={(e) => setBatchForm({...batchForm, depot_id: e.target.value})}
                >
                  <option value="">Select Depot</option>
                  {depots.map(depot => (
                    <option key={depot.id} value={depot.id}>{depot.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label">Batch Number</label>
                <input
                  type="text"
                  className="form-input bg-gray-50"
                  value={batchForm.batch_number || ''}
                  readOnly
                />
                <p className="mt-1 text-xs text-gray-500">Batch number cannot be changed</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Supplier</label>
                <input
                  type="text"
                  className="form-input"
                  value={batchForm.supplier}
                  onChange={(e) => setBatchForm({...batchForm, supplier: e.target.value})}
                />
              </div>

              <div>
                <label className="form-label">Purchase Date *</label>
                <input
                  type="date"
                  required
                  className="form-input"
                  value={batchForm.purchase_date}
                  onChange={(e) => setBatchForm({...batchForm, purchase_date: e.target.value})}
                  max={format(new Date(), 'yyyy-MM-dd')}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Initial Quantity (Cases) *</label>
                <input
                  type="number"
                  min="1"
                  required
                  className="form-input"
                  value={batchForm.initial_quantity_cases}
                  onChange={(e) => setBatchForm({...batchForm, initial_quantity_cases: parseInt(e.target.value)})}
                />
              </div>

              <div>
                <label className="form-label">Current Quantity (Cases)</label>
                <input
                  type="number"
                  min="0"
                  className="form-input bg-gray-50"
                  value={batchForm.current_quantity_cases}
                  readOnly
                />
                <p className="mt-1 text-xs text-gray-500">Auto-updated by system based on sales</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Cost per Case (ZMW) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  className="form-input"
                  value={batchForm.cost_per_case}
                  onChange={(e) => setBatchForm({...batchForm, cost_per_case: parseFloat(e.target.value)})}
                />
              </div>

              <div>
                <label className="form-label">Selling Price per Case (ZMW) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  className="form-input"
                  value={batchForm.price_per_case}
                  onChange={(e) => setBatchForm({...batchForm, price_per_case: parseFloat(e.target.value)})}
                />
              </div>
            </div>

            <div>
              <label className="form-label">Expiry Date</label>
              <input
                type="date"
                className="form-input"
                value={batchForm.expiry_date}
                onChange={(e) => setBatchForm({...batchForm, expiry_date: e.target.value})}
                min={batchForm.purchase_date}
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowEditBatchModal(false);
                  resetBatchForm();
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                Update Batch
              </button>
            </div>
          </div>
        </form>
      </Modal>

      {/* Delete Batch Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteBatchDialog}
        onClose={() => setShowDeleteBatchDialog(false)}
        onConfirm={handleDeleteBatch}
        title="Delete Batch"
        message={`Are you sure you want to delete batch ${selectedBatch?.batch_number || ''}? This action cannot be undone.`}
        confirmText="Delete"
        type="danger"
      />
    </div>
  );
};

export default ProductDetails;